import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { cn } from 'src/shared/utils/cn'

interface ParticleProps {
  id: number
  x: number
  y: number
  size: number
}

interface ParticleSwarmLoaderProps {
  color?: string
  count?: number
  className?: string
}

const ParticleSwarmLoader = ({ color, count = 50, className }: ParticleSwarmLoaderProps = {}) => {
  const [particles, setParticles] = useState<ParticleProps[]>([])

  useEffect(() => {
    setParticles(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        x: Math.random() * 200 - 100,
        y: Math.random() * 200 - 100,
        size: Math.random() * 4 + 2,
      })),
    )
  }, [count])

  // Function to get the appropriate color value
  const getColorValue = (colorName?: string) => {
    if (!colorName) return 'hsl(var(--primary))'

    // If the color is a predefined theme color, use the CSS variable
    if (colorName === 'primary') return 'hsl(var(--primary))'
    if (colorName === 'secondary') return 'hsl(var(--secondary))'
    if (colorName === 'accent') return 'hsl(var(--accent))'
    if (colorName === 'muted') return 'hsl(var(--muted))'
    if (colorName === 'secondaryInverse') return 'hsl(var(--secondary-inverse))'

    // Otherwise, use the provided color value directly
    return colorName
  }

  return (
    <div className={cn('size-40 overflow-hidden rounded-full bg-transparent', className)}>
      <svg width="100%" height="100%" viewBox="-100 -100 200 200">
        {particles.map((particle) => (
          <motion.circle
            key={particle.id}
            cx={particle.x}
            cy={particle.y}
            r={particle.size}
            fill={getColorValue(color)}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0],
              x: [0, particle.x * -0.5, 0],
              y: [0, particle.y * -0.5, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 1,
            }}
          />
        ))}
      </svg>
    </div>
  )
}

export default ParticleSwarmLoader
